import React, { useState, useEffect } from "react";
import {
  MapContainer as LeafletMap,
  TileLayer,
  CircleMarker,
  Popup,
} from "react-leaflet";
import { fetchAnalyticsData } from "../dataService";
import { Card, Modal } from "react-bootstrap";
import logo from "../images/amotive-orange-revert-white-orange-symbol.png";
import { FaUser } from "react-icons/fa";

const GOOGLE_API_KEY = "AIzaSyD16h98hJe26HkkEvzKPbH2WytgT5srggM";

/* ======================================================
   🔥 1. Bounding boxes Regioni Italiane
====================================================== */
const ITALIAN_REGIONS_BBOX = {
  Piemonte: { latMin: 44.0, latMax: 46.7, lngMin: 6.5, lngMax: 9.3 },
  "Valle d'Aosta": { latMin: 45.5, latMax: 46.6, lngMin: 6.8, lngMax: 7.9 },
  Lombardia: { latMin: 44.7, latMax: 46.7, lngMin: 8.3, lngMax: 11.5 },
  "Trentino-Alto Adige": {
    latMin: 45.8,
    latMax: 47.1,
    lngMin: 10.4,
    lngMax: 12.5,
  },
  Veneto: { latMin: 45.0, latMax: 46.8, lngMin: 10.5, lngMax: 13.2 },
  "Friuli-Venezia Giulia": {
    latMin: 45.6,
    latMax: 46.7,
    lngMin: 12.3,
    lngMax: 13.9,
  },
  Liguria: { latMin: 43.7, latMax: 44.6, lngMin: 7.4, lngMax: 10.0 },
  "Emilia-Romagna": { latMin: 43.8, latMax: 45.1, lngMin: 9.2, lngMax: 12.3 },
  Toscana: { latMin: 42.2, latMax: 44.5, lngMin: 9.0, lngMax: 12.0 },
  Marche: { latMin: 42.8, latMax: 44.6, lngMin: 12.2, lngMax: 13.8 },
  Umbria: { latMin: 42.4, latMax: 43.6, lngMin: 11.7, lngMax: 13.2 },
  Lazio: { latMin: 40.8, latMax: 42.7, lngMin: 11.0, lngMax: 13.6 },
  Abruzzo: { latMin: 41.8, latMax: 42.9, lngMin: 13.1, lngMax: 14.5 },
  Molise: { latMin: 41.4, latMax: 42.0, lngMin: 14.3, lngMax: 15.2 },
  Campania: { latMin: 40.0, latMax: 41.5, lngMin: 13.7, lngMax: 15.1 },
  Puglia: { latMin: 39.7, latMax: 41.9, lngMin: 15.2, lngMax: 18.6 },
  Basilicata: { latMin: 39.8, latMax: 40.9, lngMin: 15.5, lngMax: 16.9 },
  Calabria: { latMin: 37.9, latMax: 40.2, lngMin: 15.6, lngMax: 17.2 },
  Sicilia: { latMin: 36.6, latMax: 38.3, lngMin: 12.3, lngMax: 15.7 },
  Sardegna: { latMin: 38.7, latMax: 41.4, lngMin: 8.0, lngMax: 9.8 },
};

/* ======================================================
   🔥 2. Funzione unica per ottenere regione da coordinate
====================================================== */
function getItalianRegionFromCoords(lat, lng) {
  for (const [region, box] of Object.entries(ITALIAN_REGIONS_BBOX)) {
    if (
      lat >= box.latMin &&
      lat <= box.latMax &&
      lng >= box.lngMin &&
      lng <= box.lngMax
    ) {
      return region;
    }
  }
  return null; // fuori Italia
}

/* ======================================================
   🔥 3. Estrattore Regione da Google
====================================================== */
function extractRegionFromGoogle(components) {
  const admin1 = components.find((c) =>
    c.types.includes("administrative_area_level_1"),
  );

  if (admin1?.long_name) return admin1.long_name;
  if (admin1?.short_name) return admin1.short_name;

  return null;
}

/* ======================================================
   🔥 4. Cache + Geocoding definitivo
====================================================== */
const loadCache = () =>
  JSON.parse(localStorage.getItem("geocodeCache") || "{}");
const saveCache = (cache) =>
  localStorage.setItem("geocodeCache", JSON.stringify(cache));

async function getCoordinatesCached(city, country) {
  const cache = loadCache();
  const key = `${city}-${country}`;

  if (cache[key]) return cache[key];

  const url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
    city + "," + country,
  )}&key=${GOOGLE_API_KEY}`;

  const res = await fetch(url);
  const data = await res.json();

  if (data.status !== "OK" || !data.results.length) return null;

  const result = data.results[0];
  const location = result.geometry.location;
  const components = result.address_components;

  let region = extractRegionFromGoogle(components);

  // Se è Italia e Google non da regione, calcola fallback
  if (!region && country.toLowerCase() === "italy") {
    region = getItalianRegionFromCoords(location.lat, location.lng);
  }

  const coords = {
    lat: location.lat,
    lng: location.lng,
    region: region || "Unknown",
  };

  cache[key] = coords;
  saveCache(cache);

  return coords;
}

/* ======================================================
   🔥 5. Legenda
====================================================== */
const MapLegend = ({ topRegions }) => (
  <div
    style={{
      position: "absolute",
      top: 10,
      right: 10,
      zIndex: 1000,
      background: "rgba(0,0,0,0.7)",
      padding: 10,
      borderRadius: 8,
      color: "white",
      width: 180,
      fontSize: 14,
      pointerEvents: "none",
    }}
  >
    <h6 style={{ textAlign: "center", marginBottom: 8 }}>Top 10 Regioni</h6>
    {topRegions.map((item, i) => (
      <div
        key={i}
        style={{
          display: "flex",
          justifyContent: "space-between",
          borderBottom: "1px solid rgba(255,255,255,0.2)",
          padding: "3px 0",
        }}
      >
        <span>
          {i + 1}. {item.region}
        </span>
        <strong>{item.visits}</strong>
      </div>
    ))}
  </div>
);

/* ======================================================
   🔥 6. COMPONENTE PRINCIPALE
====================================================== */
const MapComponent = () => {
  const [citiesData, setCitiesData] = useState([]);
  const [topRegions, setTopRegions] = useState([]);
  const [showRanking, setShowRanking] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [onlineUsers, setOnlineUsers] = useState(0);

  useEffect(() => {
    const loadTPData = async () => {
      const analytics = await fetchAnalyticsData();
      const tpData = analytics?.AR?.mappa;
      if (!tpData) return;

      const combinedData = [
        ...tpData.ecommerce.map((c) => ({ ...c, tipo: "Ecommerce" })),
        ...tpData.istituzionale.map((c) => ({ ...c, tipo: "Istituzionale" })),
      ];

      const coordinatesArray = [];

      for (const city of combinedData) {
        const coords = await getCoordinatesCached(
          city.descrizioneCity,
          city.descrizioneCountry,
        );
        if (!coords) continue;

        // Fallback definitivo per Italia
        const region =
          coords.region || getItalianRegionFromCoords(coords.lat, coords.lng);

        // Se la regione non è disponibile, scarta la città
        if (!region) continue;

        coordinatesArray.push({
          name: city.descrizioneCity,
          position: coords,
          visits: Number(city.activeUser),
          tipo: city.tipo,
          region: region,
        });
      }

      // Filtra eventuali regioni vuote, solo per sicurezza
      const filteredCoordinates = coordinatesArray.filter(
        (c) => c.region && c.region !== "Unknown",
      );

      setCitiesData(filteredCoordinates);

      // Raggruppa regioni per legenda
      const regionCounts = {};
      filteredCoordinates.forEach((c) => {
        if (!regionCounts[c.region]) regionCounts[c.region] = 0;
        regionCounts[c.region] += c.visits;
      });

      const top = Object.entries(regionCounts)
        .map(([region, visits]) => ({ region, visits }))
        .sort((a, b) => b.visits - a.visits)
        .slice(0, 10);

      setTopRegions(top);
    };
    loadTPData();
  }, []);

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCliente(null);
  };

  useEffect(() => {
    const fetchOnlineUsers = async () => {
      try {
        const response = await fetch(
          "ws/index.php?Command=google.getAnalitycs",
        );
        const data = await response.json();

        // ✅ Estrae la sezione AR
        const arData = data?.AR?.mappa;
        if (arData) {
          const ecommerce = arData.ecommerce || [];
          const istituzionale = arData.istituzionale || [];

          console.log("Dati ricevuti per AR:", { ecommerce, istituzionale });

          // ✅ Somma tutti gli activeUser (sia ecommerce che istituzionale)
          const totalUsers = [...ecommerce, ...istituzionale].reduce(
            (sum, item) => {
              const val = parseInt(item.activeUser, 10);
              console.log(
                `Città: ${
                  item.descrizioneCity || "(sconosciuta)"
                } - activeUser: ${val}`,
              );
              return sum + (isNaN(val) ? 0 : val);
            },
            0,
          );

          console.log("Totale utenti online AR:", totalUsers);
          setOnlineUsers(totalUsers);
        } else {
          console.warn(
            "⚠️ Nessun dato 'AR.mappa' trovato nell'oggetto ricevuto.",
          );
          setOnlineUsers(0);
        }
      } catch (error) {
        console.error("❌ Errore nel recupero utenti online:", error);
      }
    };

    fetchOnlineUsers();
    const interval = setInterval(fetchOnlineUsers, 60000); // aggiorna ogni minuto
    return () => clearInterval(interval);
  }, []);

  return (
    <Card className="hover-effect bg-dark" style={{ minHeight: "230px" }}>
      <div
        className="text-title2 mx-auto mt-1"
        style={{
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => setShowRanking(true)}
      >
        <strong className="text-title2">
          <FaUser
            className="me-2"
            style={{ color: "rgba(241,111,36,1)", marginTop: "1px" }}
          />
          UTENTI ATTIVI ONLINE: {onlineUsers}
        </strong>
      </div>

      <LeafletMap
        style={{ height: "100%", width: "100%" }}
        center={[41.8719, 12.5674]}
        zoom={5}
        scrollWheelZoom={true}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
          attribution="&copy; OpenStreetMap contributors &copy; CARTO"
        />

        {citiesData.map((city, i) => (
          <CircleMarker
            key={i}
            center={[city.position.lat, city.position.lng]}
            pathOptions={{
              color: "transparent",
              fillColor: city.tipo === "Ecommerce" ? "#fd7e14" : "#ffc107",
              fillOpacity: 0.7,
            }}
            radius={Math.max(8, Math.sqrt(city.visits) * 1.5)}
          >
            <Popup>
              <h5>{city.name}</h5>
              <p>Visite: {city.visits}</p>
              <p>Tipo: {city.tipo}</p>
              <p>Regione: {city.region}</p>
            </Popup>
          </CircleMarker>
        ))}
      </LeafletMap>

      <Modal
        show={showRanking}
        onHide={() => setShowRanking(false)}
        centered
        className="p-5"
      >
        <Modal.Header className="bg-dark rounded-0">
          <Modal.Title className="text-modal2AR mx-auto bg-transparent">
            Top 10 Regioni
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="bg-dark text-light">
          {topRegions.length === 0 ? (
            <p className="text-center fs-4">Nessun dato disponibile</p>
          ) : (
            topRegions.map((item, i) => (
              <p
                key={i}
                className="text-center fs-4"
                style={{
                  borderBottom: "1px solid rgba(255,255,255,0.2)",
                  padding: "6px 0",
                }}
              >
                <span className="fw-bold">
                  {i + 1}. {item.region}:{" "}
                </span>{" "}
                {item.visits}
              </p>
            ))
          )}
        </Modal.Body>

        <Modal.Footer className="bg-dark rounded-0">
          {/* Puoi mettere un logo o lasciare vuoto */}
          <img
            src={logo}
            alt=""
            className="mx-auto p-2"
            style={{ width: "180px" }}
          />
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default MapComponent;
